import type { FirebaseApp } from "firebase/app";
import type { FeatureFlags, FeatureOverrides } from "../env/featureFlags.js";

/** RemoteConfig 파라미터 키 — Firebase 콘솔에서 이 키로 boolean을 내리면 킬스위치 작동. */
const RC_KEYS: Record<keyof FeatureFlags, string> = {
  dailyShop: "feature_dailyShop",
  dungeon: "feature_dungeon",
  houseAds: "feature_houseAds",
  moduleFusion: "feature_moduleFusion",
  relic: "feature_relic",
  iapRemoveAds: "feature_iapRemoveAds",
  banner: "feature_banner",
};

/**
 * Firebase RemoteConfig에서 기능 킬스위치 override를 가져온다 (DIP — firebase 의존 격리).
 *
 * - **콘솔에 키가 있을 때만**(source==="remote") override로 취급 — 키 미설정이면 정적 기본값 유지.
 * - OFF-only 병합은 resolveFeatureFlags가 보장하므로 여기선 값 그대로 반환한다.
 * - fetch 실패/타임아웃(3s)/미지원(WebView indexedDB 부재 등) 시 빈 override — 부팅을 막지 않는다
 *   (firebaseClient.ts와 동일한 graceful fallback 정신).
 * - 모듈은 dynamic import — 기능 전부 꺼진 웹 live는 호출 자체가 없어 RC 코드가 로드되지 않는다.
 */
export async function fetchFeatureOverrides(
  app: FirebaseApp,
  base: FeatureFlags,
): Promise<FeatureOverrides> {
  try {
    const { getRemoteConfig, fetchAndActivate, getValue, isSupported } = await import(
      "firebase/remote-config"
    );
    if (!(await isSupported())) return {};
    const rc = getRemoteConfig(app);
    rc.settings.fetchTimeoutMillis = 3000;
    // 킬스위치 반영 지연 상한 1h — 기본 12h는 긴급 차단 용도에 너무 길다.
    rc.settings.minimumFetchIntervalMillis = 60 * 60 * 1000;
    rc.defaultConfig = {
      [RC_KEYS.dailyShop]: base.dailyShop,
      [RC_KEYS.dungeon]: base.dungeon,
      [RC_KEYS.houseAds]: base.houseAds,
      [RC_KEYS.moduleFusion]: base.moduleFusion,
      [RC_KEYS.relic]: base.relic,
      [RC_KEYS.iapRemoveAds]: base.iapRemoveAds,
      [RC_KEYS.banner]: base.banner,
    };
    await fetchAndActivate(rc);
    const overrides: Partial<Record<keyof FeatureFlags, boolean>> = {};
    for (const key of Object.keys(RC_KEYS) as (keyof FeatureFlags)[]) {
      const value = getValue(rc, RC_KEYS[key]);
      if (value.getSource() === "remote") overrides[key] = value.asBoolean();
    }
    return overrides;
  } catch (error) {
    console.warn(
      "[firebase] remote-config fetch skipped — static feature flags:",
      (error as Error).message,
    );
    return {};
  }
}
